/**
 * Proceduralne tekstury Szkoły piesków — gdy brak PNG obj_school_*.
 * Zero napisów / emoji tekstowych; pastelowe kształty (wzorzec: park/playgroundVisuals).
 */
import Phaser from 'phaser';
import { SCHOOL_OBJECT_DEFS } from './objects.js';

const TEX_SIZE = 160;

export type SchoolDecorKind =
  | 'backpack'
  | 'crayon'
  | 'eraser'
  | 'block_a'
  | 'block_b'
  | 'block_c'
  | 'block_d'
  | 'bell'
  | 'desk'
  | 'globe';

/** Mapa texture key → rodzaj rysunku. */
export const SCHOOL_TEXTURE_SHAPES: Readonly<
  Record<string, SchoolDecorKind>
> = {
  obj_school_backpack: 'backpack',
  obj_school_crayon: 'crayon',
  obj_school_eraser: 'eraser',
  obj_school_block_a: 'block_a',
  obj_school_block_b: 'block_b',
  obj_school_block_c: 'block_c',
  obj_school_block_d: 'block_d',
  obj_school_bell: 'bell',
  obj_school_desk: 'desk',
  obj_school_globe: 'globe',
};

/**
 * Upewnij się, że wszystkie tekstury szkoły istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie.
 */
export function ensureSchoolTextures(scene: Phaser.Scene): void {
  for (const def of SCHOOL_OBJECT_DEFS) {
    ensureSchoolTexture(scene, def.texture);
  }
}

export function ensureSchoolTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = SCHOOL_TEXTURE_SHAPES[textureKey];
  if (!kind) return textureKey;

  const g = scene.make.graphics({ x: 0, y: 0 });
  drawSchoolShape(g, kind, TEX_SIZE, TEX_SIZE);
  g.generateTexture(textureKey, TEX_SIZE, TEX_SIZE);
  g.destroy();
  return textureKey;
}

/** Czysty opis kształtu (testowalny). */
export function schoolShapeForTexture(
  textureKey: string,
): SchoolDecorKind | null {
  return SCHOOL_TEXTURE_SHAPES[textureKey] ?? null;
}

export function schoolTextureKeys(): string[] {
  return Object.keys(SCHOOL_TEXTURE_SHAPES);
}

export function drawSchoolShape(
  g: Phaser.GameObjects.Graphics,
  kind: SchoolDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;

  switch (kind) {
    case 'backpack':
      drawBackpack(g, cx, cy);
      break;
    case 'crayon':
      drawCrayon(g, cx, cy);
      break;
    case 'eraser':
      drawEraser(g, cx, cy);
      break;
    case 'block_a':
      drawBlock(g, cx, cy, 0xef9a9a, 'A');
      break;
    case 'block_b':
      drawBlock(g, cx, cy, 0x90caf9, 'B');
      break;
    case 'block_c':
      drawBlock(g, cx, cy, 0xa5d6a7, 'C');
      break;
    case 'block_d':
      drawBlock(g, cx, cy, 0xffe082, 'D');
      break;
    case 'bell':
      drawSchoolBell(g, cx, cy);
      break;
    case 'desk':
      drawDesk(g, cx, cy);
      break;
    case 'globe':
      drawGlobe(g, cx, cy);
      break;
  }
}

function drawBackpack(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // korpus
  g.fillStyle(0x42a5f5, 1);
  g.fillRoundedRect(cx - 36, cy - 36, 72, 80, 14);
  g.lineStyle(4, 0xffffff, 0.95);
  g.strokeRoundedRect(cx - 36, cy - 36, 72, 80, 14);
  // klapa
  g.fillStyle(0x1e88e5, 1);
  g.fillRoundedRect(cx - 32, cy - 40, 64, 28, 10);
  // kieszeń
  g.fillStyle(0x64b5f6, 1);
  g.fillRoundedRect(cx - 22, cy + 4, 44, 28, 8);
  // zamek
  g.fillStyle(0xffd54f, 1);
  g.fillCircle(cx, cy - 20, 7);
  // szelki
  g.fillStyle(0x1565c0, 1);
  g.fillRect(cx - 28, cy - 48, 10, 20);
  g.fillRect(cx + 18, cy - 48, 10, 20);
  // cień
  g.fillStyle(0x90caf9, 0.4);
  g.fillEllipse(cx, cy + 52, 60, 14);
}

function drawCrayon(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // kredka skośna
  g.fillStyle(0xef5350, 1);
  g.fillRoundedRect(cx - 12, cy - 48, 24, 80, 6);
  g.fillStyle(0xffcdd2, 1);
  g.fillRoundedRect(cx - 8, cy - 40, 16, 50, 4);
  // czubek
  g.fillStyle(0xc62828, 1);
  g.fillTriangle(cx - 12, cy - 48, cx + 12, cy - 48, cx, cy - 68);
  // etykieta (pasek, bez napisu)
  g.fillStyle(0xffffff, 0.9);
  g.fillRect(cx - 12, cy + 8, 24, 12);
  g.fillStyle(0xffeb3b, 1);
  g.fillRect(cx - 12, cy + 12, 24, 4);
}

function drawEraser(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xf8bbd0, 1);
  g.fillRoundedRect(cx - 36, cy - 18, 72, 36, 8);
  g.lineStyle(3, 0xffffff, 0.95);
  g.strokeRoundedRect(cx - 36, cy - 18, 72, 36, 8);
  g.fillStyle(0xfce4ec, 1);
  g.fillRoundedRect(cx - 30, cy - 10, 60, 12, 4);
  g.fillStyle(0xce93d8, 0.7);
  g.fillEllipse(cx, cy + 28, 50, 12);
}

/**
 * Klocek literowy — kształt + pasek (bez napisów w teksturze;
 * litery rozróżniają kolory: A czerwony, B niebieski, C zielony, D żółty).
 * Parametr letter zachowany dla czytelności mapowania, nie rysowany.
 */
function drawBlock(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  color: number,
  _letter: string,
): void {
  g.fillStyle(color, 1);
  g.fillRoundedRect(cx - 36, cy - 36, 72, 72, 12);
  g.lineStyle(5, 0xffffff, 0.95);
  g.strokeRoundedRect(cx - 36, cy - 36, 72, 72, 12);
  // dekor: 3 kropki (bez litery — plan: bez napisów w teksturach)
  g.fillStyle(0xffffff, 0.85);
  g.fillCircle(cx - 12, cy - 8, 6);
  g.fillCircle(cx + 12, cy - 8, 6);
  g.fillCircle(cx, cy + 10, 8);
  g.fillStyle(0x000000, 0.08);
  g.fillEllipse(cx, cy + 48, 56, 12);
}

function drawSchoolBell(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // dzwonek szkolny na ramieniu
  g.fillStyle(0xb0bec5, 1);
  g.fillRect(cx - 4, cy - 56, 8, 28);
  g.fillStyle(0xffd54f, 1);
  g.fillEllipse(cx, cy + 4, 56, 42);
  g.fillTriangle(cx - 28, cy - 4, cx + 28, cy - 4, cx, cy - 40);
  g.fillStyle(0xffecb3, 0.85);
  g.fillEllipse(cx - 6, cy - 10, 16, 12);
  g.fillStyle(0xff8a65, 1);
  g.fillCircle(cx, cy + 18, 6);
  g.fillStyle(0x90a4ae, 1);
  g.fillRoundedRect(cx - 20, cy - 60, 40, 10, 4);
}

function drawDesk(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // blat
  g.fillStyle(0xd7a86e, 1);
  g.fillRoundedRect(cx - 52, cy - 16, 104, 28, 6);
  g.lineStyle(3, 0xffffff, 0.85);
  g.strokeRoundedRect(cx - 52, cy - 16, 104, 28, 6);
  // nogi
  g.fillStyle(0xa1887f, 1);
  g.fillRect(cx - 44, cy + 10, 10, 36);
  g.fillRect(cx + 34, cy + 10, 10, 36);
  // siedzisko (ławka)
  g.fillStyle(0xffcc80, 1);
  g.fillRoundedRect(cx - 40, cy + 20, 80, 14, 4);
  // książka na blacie
  g.fillStyle(0x81c784, 1);
  g.fillRoundedRect(cx - 16, cy - 28, 32, 14, 3);
  g.fillStyle(0xffffff, 0.5);
  g.fillRect(cx - 12, cy - 24, 24, 3);
  g.fillStyle(0xc68642, 0.4);
  g.fillEllipse(cx, cy + 52, 80, 14);
}

function drawGlobe(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // kula
  g.fillStyle(0x4fc3f7, 1);
  g.fillCircle(cx, cy - 8, 40);
  g.fillStyle(0x66bb6a, 1);
  g.fillEllipse(cx - 10, cy - 16, 28, 18);
  g.fillEllipse(cx + 12, cy + 4, 22, 16);
  g.fillEllipse(cx - 4, cy + 12, 18, 12);
  g.lineStyle(3, 0xffffff, 0.7);
  g.strokeCircle(cx, cy - 8, 40);
  // oś / stójka
  g.lineStyle(4, 0xbdbdbd, 1);
  g.lineBetween(cx, cy - 50, cx, cy + 28);
  g.fillStyle(0xa1887f, 1);
  g.fillEllipse(cx, cy + 36, 36, 12);
  g.fillRoundedRect(cx - 8, cy + 24, 16, 14, 3);
}
